<template>
  <div class="scoring-mapping-manager">
    <h1>Scoring Mapping (Dinamis)</h1>
    <p class="subtitle">Atur Tabel Norma dan Formula perhitungan untuk setiap Template Psikogram.</p>

    <!-- TAB NAVIGASI -->
    <div class="tabs">
      <button :class="{ active: activeTab === 'norm' }" @click="activeTab = 'norm'">1. Tabel Norma</button>
      <button :class="{ active: activeTab === 'formula' }" @click="activeTab = 'formula'">2. Formula Aspek & IQ</button>
    </div>

    <!-- TAB 1: TABEL NORMA -->
    <div v-if="activeTab === 'norm'" class="tab-content">
      <div class="card">
        <h2>Manajemen Tabel Norma</h2>
        <form @submit.prevent="createNormTable" class="inline-form">
          <input v-model="newNormTable.name" placeholder="Nama Tabel Norma (Misal: Norma IST Usia 20-25)" required />
          <input v-model="newNormTable.description" placeholder="Deskripsi Singkat" />
          <button type="submit" :disabled="isLoading">Buat Tabel Norma</button>
        </form>
        <hr>

        <div class="norm-list">
          <div v-for="table in normTables" :key="table.id" class="norm-card">
            <h3>{{ table.name }}</h3>
            <p>{{ table.description }}</p>
            <button @click="selectedNormTable = table" class="btn-secondary">Kelola Data</button>
            <button @click="deleteNormTable(table.id)" class="delete-btn small" style="margin-left: 10px;">Hapus</button>
          </div>
        </div>
      </div>

      <div v-if="selectedNormTable" class="card">
        <h2>Data Norma: {{ selectedNormTable.name }}</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Raw Score Min</th>
              <th>Raw Score Max</th>
              <th>Standard Score (SW / SS)</th>
              <th>Kategori (R/K/C/B/T)</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="data in selectedNormTable.data" :key="data.id">
              <!-- Mode Edit -->
              <template v-if="editRowId === data.id">
                <td><input v-model.number="editRowData.raw_score_min" type="number" required class="small-input"/></td>
                <td><input v-model.number="editRowData.raw_score_max" type="number" required class="small-input"/></td>
                <td><input v-model.number="editRowData.standard_score" type="number" required class="small-input"/></td>
                <td><input v-model="editRowData.category" type="text" class="small-input"/></td>
                <td>
                  <button @click="saveEditNormData" class="btn-primary micro">Simpan</button>
                  <button @click="cancelEdit" class="btn-secondary micro" style="margin-left:5px;">Batal</button>
                </td>
              </template>
              
              <!-- Mode Tampil -->
              <template v-else>
                <td>{{ data.raw_score_min }}</td>
                <td>{{ data.raw_score_max }}</td>
                <td>{{ data.standard_score }}</td>
                <td>{{ data.category || '-' }}</td>
                <td>
                  <button @click="startEdit(data)" class="btn-secondary micro">Edit</button>
                  <button @click="deleteNormData(data.id)" class="delete-btn micro" style="margin-left:5px;">Hapus</button>
                </td>
              </template>
            </tr>
            <!-- Baris Tambah Data -->
            <tr>
              <td><input v-model.number="newNormData.raw_score_min" type="number" required class="small-input"/></td>
              <td><input v-model.number="newNormData.raw_score_max" type="number" required class="small-input"/></td>
              <td><input v-model.number="newNormData.standard_score" type="number" required class="small-input"/></td>
              <td><input v-model="newNormData.category" type="text" placeholder="A/B/C" class="small-input"/></td>
              <td><button @click="addNormData" class="btn-primary micro">Tambah</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- TAB 2: FORMULA SKORING -->
    <div v-if="activeTab === 'formula'" class="tab-content">
      <div class="card">
        <h2>Pilih Template Psikogram</h2>
        <select v-model="selectedTemplateId" @change="fetchTemplateDetails" class="main-select">
          <option :value="null">-- Pilih Template --</option>
          <option v-for="tpl in templates" :key="tpl.id" :value="tpl.id">{{ tpl.name }}</option>
        </select>
      </div>

      <div v-if="selectedTemplateId" class="card">
        <h2>Atur Formula Skoring</h2>
        <p class="subtitle">Gunakan format formula seperti: <code>[TEST_1_RAW] * 2</code> atau memanggil tabel norma <code>NORM([TEST_1_RAW], ID_TABEL)</code></p>
        
        <table class="data-table">
          <thead>
            <tr>
              <th>Sub-Aspek / Target</th>
              <th>Tipe</th>
              <th>Formula Saat Ini</th>
              <th>Norma Akhir (Opsional)</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <!-- Daftar Sub Aspek -->
            <tr v-for="aspect in currentTemplateAspects" :key="'aspect-' + aspect.id">
              <td colspan="5" class="aspect-header-row"><strong>{{ aspect.name }}</strong></td>
            </tr>
            <template v-for="aspect in currentTemplateAspects" :key="'group-' + aspect.id">
              <tr v-for="sub in aspect.sub_aspects" :key="sub.id">
                <td style="padding-left: 20px;">- {{ sub.name }}</td>
                <td>Sub-Aspek</td>
                <td>
                  <input v-if="formulaDrafts[sub.id]" v-model="formulaDrafts[sub.id].formula_expression" placeholder="Contoh: [TEST_5_RAW]" class="formula-input" />
                  
                  <!-- Chip Pilihan Variabel Otomatis -->
                  <div v-if="sub.tests && sub.tests.length > 0" style="margin-top: 5px;">
                    <span style="font-size: 0.7rem; color: #666; display: block; margin-bottom: 2px;">Variabel tersedia (Klik untuk insert):</span>
                    <span v-for="test in sub.tests" :key="test.id + '_sw'" @click="insertVar(sub.id, test.name + '_SW')" class="var-chip" title="Standard Score / Skor Matang">
                      {{ test.name }}_SW
                    </span>
                    <span v-for="test in sub.tests" :key="test.id + '_rw'" @click="insertVar(sub.id, test.name + '_RW')" class="var-chip var-chip-rw" title="Raw Score / Skor Mentah">
                      {{ test.name }}_RW
                    </span>
                  </div>
                  <div v-else style="margin-top: 5px; font-size: 0.7rem; color: #999;">
                    <em>Belum ada tes terhubung.</em>
                  </div>

                </td>
                <td>
                  <select v-if="formulaDrafts[sub.id]" v-model="formulaDrafts[sub.id].norm_table_id">
                    <option :value="null">-- Tidak Ada --</option>
                    <option v-for="table in normTables" :key="table.id" :value="table.id">{{ table.name }}</option>
                  </select>
                </td>
                <td>
                  <button @click="saveFormula(sub.id, 'sub_aspect')" class="btn-primary micro">Simpan</button>
                </td>
              </tr>
            </template>
            <!-- Target Khusus: IQ -->
            <tr>
              <td colspan="5" class="aspect-header-row"><strong>Skor Khusus (Keseluruhan)</strong></td>
            </tr>
            <tr>
              <td style="padding-left: 20px;"><strong>IQ Total</strong></td>
              <td>Kalkulasi Khusus</td>
              <td>
                  <input v-if="formulaDrafts['iq']" v-model="formulaDrafts['iq'].formula_expression" placeholder="Contoh: SUM([TEST_1_SW], [TEST_2_SW])" class="formula-input" />
              </td>
              <td>
                  <select v-if="formulaDrafts['iq']" v-model="formulaDrafts['iq'].norm_table_id">
                    <option :value="null">-- Tidak Ada --</option>
                    <option v-for="table in normTables" :key="table.id" :value="table.id">{{ table.name }}</option>
                  </select>
              </td>
              <td>
                  <button @click="saveFormula('iq', 'iq')" class="btn-primary micro">Simpan</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import apiClient from '@/services/api.js';

const activeTab = ref('norm');
const isLoading = ref(false);

// State Tab 1
const normTables = ref([]);
const selectedNormTable = ref(null);
const newNormTable = ref({ name: '', description: '' });
const newNormData = ref({ raw_score_min: 0, raw_score_max: 0, standard_score: 0, category: '' });

// State untuk Edit Baris
const editRowId = ref(null);
const editRowData = ref({ raw_score_min: 0, raw_score_max: 0, standard_score: 0, category: '' });

// State Tab 2
const templates = ref([]);
const selectedTemplateId = ref(null);
const currentTemplateAspects = ref([]);
const existingMappings = ref([]);

// Menyimpan draft form input (key berupa sub_aspect_id, khusus IQ pakai string 'iq')
const formulaDrafts = reactive({});

// Initial fetch
onMounted(async () => {
  await fetchNormTables();
  await fetchTemplates();
});

const fetchNormTables = async () => {
  try {
    const res = await apiClient.get('/admin/norm-tables');
    normTables.value = res.data;
    if (selectedNormTable.value) {
      selectedNormTable.value = normTables.value.find(n => n.id === selectedNormTable.value.id);
    }
  } catch (error) { console.error(error); }
};

const createNormTable = async () => {
  isLoading.value = true;
  try {
    await apiClient.post('/admin/norm-tables', newNormTable.value);
    newNormTable.value = { name: '', description: '' };
    await fetchNormTables();
  } catch (error) { alert("Gagal membuat tabel norma"); }
  finally { isLoading.value = false; }
};

const deleteNormTable = async (id) => {
  if(!confirm("Hapus tabel norma ini?")) return;
  try {
    await apiClient.delete(`/admin/norm-tables/${id}`);
    if (selectedNormTable.value?.id === id) selectedNormTable.value = null;
    await fetchNormTables();
  } catch (error) { alert("Gagal menghapus"); }
};

const addNormData = async () => {
  if (!selectedNormTable.value) return;
  try {
    await apiClient.post(`/admin/norm-tables/${selectedNormTable.value.id}/data`, newNormData.value);
    newNormData.value = { raw_score_min: 0, raw_score_max: 0, standard_score: 0, category: '' };
    await fetchNormTables(); // refresh data
  } catch (error) { alert("Gagal menambah data norma"); }
};

const deleteNormData = async (id) => {
  try {
    await apiClient.delete(`/admin/norm-data/${id}`);
    await fetchNormTables();
  } catch (error) { alert("Gagal menghapus data"); }
};

const startEdit = (data) => {
  editRowId.value = data.id;
  editRowData.value = { ...data };
};

const cancelEdit = () => {
  editRowId.value = null;
};

const saveEditNormData = async () => {
  try {
    await apiClient.put(`/admin/norm-data/${editRowId.value}`, editRowData.value);
    editRowId.value = null;
    await fetchNormTables();
  } catch (error) {
    alert("Gagal menyimpan perubahan");
    console.error(error);
  }
};


// --- Tab 2 Logika ---
const fetchTemplates = async () => {
  try {
    const res = await apiClient.get('/admin/psychogram-templates/');
    templates.value = res.data;
  } catch (err) { console.error(err); }
};

const fetchTemplateDetails = async () => {
  if (!selectedTemplateId.value) return;
  try {
    // Ambil aspek dari template ini
    const aspectsRes = await apiClient.get(`/admin/templates/${selectedTemplateId.value}/aspects/`);
    const fetchedAspects = aspectsRes.data;
    
    // Ambil mappings yang sudah ada
    const mapsRes = await apiClient.get(`/admin/psychogram-templates/${selectedTemplateId.value}/scoring-mappings`);
    existingMappings.value = mapsRes.data;

    // Inisialisasi form drafts DULU sebelum update reactive currentTemplateAspects
    fetchedAspects.forEach(aspect => {
      aspect.sub_aspects.forEach(sub => {
        const existingMap = existingMappings.value.find(m => m.sub_aspect_id === sub.id && m.target_type === 'sub_aspect');
        formulaDrafts[sub.id] = {
          formula_expression: existingMap ? existingMap.formula_expression : '',
          norm_table_id: existingMap ? existingMap.norm_table_id : null,
          mapping_id: existingMap ? existingMap.id : null
        };
      });
    });

    // Baru kita update template aspects agar Vue render dengan drafts yang sudah siap
    currentTemplateAspects.value = fetchedAspects;

    // Inisialisasi untuk IQ
    const iqMap = existingMappings.value.find(m => m.target_type === 'iq');
    formulaDrafts['iq'] = {
      formula_expression: iqMap ? iqMap.formula_expression : '',
      norm_table_id: iqMap ? iqMap.norm_table_id : null,
      mapping_id: iqMap ? iqMap.id : null
    };

  } catch (error) { console.error(error); }
};

// Helper function untuk insert teks ke formula
const insertVar = (subId, varName) => {
  if (formulaDrafts[subId]) {
    // Jika masih kosong, langsung set. Jika sudah ada isinya, tambahkan di belakang dengan spasi +
    if (!formulaDrafts[subId].formula_expression) {
      formulaDrafts[subId].formula_expression = varName;
    } else {
      formulaDrafts[subId].formula_expression += ` + ${varName}`;
    }
  }
};

const saveFormula = async (targetId, type) => {
  const draft = formulaDrafts[targetId];
  const payload = {
    psychogram_template_id: selectedTemplateId.value,
    sub_aspect_id: type === 'sub_aspect' ? targetId : null,
    target_type: type,
    formula_expression: draft.formula_expression,
    norm_table_id: draft.norm_table_id
  };

  try {
    if (draft.mapping_id) {
      // Jika sudah ada, hapus dulu lalu buat baru (Atau update jika ada API update)
      await apiClient.delete(`/admin/scoring-mappings/${draft.mapping_id}`);
    }
    if (draft.formula_expression) {
       const res = await apiClient.post('/admin/scoring-mappings', payload);
       draft.mapping_id = res.data.id;
       alert("Berhasil disimpan!");
    } else {
       alert("Formula dihapus.");
       draft.mapping_id = null;
    }
  } catch (error) {
    alert("Gagal menyimpan formula");
    console.error(error);
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

/* Tambahkan style untuk chip variabel */
.var-chip {
  display: inline-block;
  background-color: #e0f2f1;
  color: #00796b;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 12px;
  font-size: 0.75rem;
  cursor: pointer;
  border: 1px solid #b2dfdb;
  transition: background-color 0.2s;
}
.var-chip:hover {
  background-color: #b2dfdb;
}
.var-chip-rw {
  background-color: #fce4ec;
  color: #c2185b;
  border-color: #f8bbd0;
}
.var-chip-rw:hover {
  background-color: #f8bbd0;
}
.scoring-mapping-manager { max-width: 1200px; margin: auto; padding: 30px; }
.subtitle { color: #555; margin-bottom: 25px; }

.tabs { display: flex; gap: 10px; margin-bottom: 20px; }
.tabs button { padding: 10px 20px; border: none; background: #eee; cursor: pointer; border-radius: 5px 5px 0 0; font-weight: bold; }
.tabs button.active { background: #1abc9c; color: white; }

.card { margin-bottom: 25px; background: #fff; padding: 25px; border-radius: 10px; box-shadow: 0 3px 10px rgba(0,0,0,0.06); }

.inline-form { display: flex; gap: 10px; align-items: center; }
.inline-form input { flex-grow: 1; padding: 10px; border-radius: 6px; border: 1px solid #ccc; }
.inline-form button { padding: 10px 15px; background-color: #3498db; color: white; border: none; border-radius: 6px; }

.norm-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 15px; }
.norm-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; background: #f9f9f9; }

.data-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
.data-table th, .data-table td { border: 1px solid #eee; padding: 10px; text-align: left; }
.data-table th { background: #f5f6fa; }

.aspect-header-row { background: #e8f6f3 !important; }
.small-input { width: 80px; padding: 5px; }
.formula-input { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; }

.btn-primary { background: #1abc9c; color: white; border: none; padding: 5px 10px; cursor: pointer; border-radius: 4px; }
.btn-secondary { background: #34495e; color: white; border: none; padding: 5px 10px; cursor: pointer; border-radius: 4px; }
.delete-btn { background: #e74c3c; color: white; border: none; padding: 5px 10px; cursor: pointer; border-radius: 4px; }
.micro { font-size: 0.8em; padding: 3px 8px; }
.main-select { padding: 10px; width: 300px; font-size: 1rem; border-radius: 5px;}
</style>
